import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { AutomationEditorStep } from '../../../../models/Automations';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateAddTriggerMutation,
	useAutomationTemplateAllowUnsubscribedMutation,
	useAutomationTemplateSendToHouseholdMutation,
	useCancelCriteriaContactFilterMutation,
} from '../../../../queries';
import { AdvancedFiltersContextProvider } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { baseStyleSheet } from '../../../styles/styles';
import { AdvancedFiltersLegacyUI } from '../../AdvancedFilters/AdvancedFiltersLegacyUI';
import { Checkbox } from '../../Checkbox';
import { Collapsible } from '../../Collapsible';
import { FlyoutModal } from '../../FlyoutModal';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationTriggerIcon } from '../../svgs/icons/AutomationTriggerIcon';
import { AdvancedOptionsButton } from '../AutomationStepCard/presentation';
import { AutomationCancelOnReplyCheckbox, AutomationTriggerSettings } from './presentation';
import { styleSheet } from './styles';

interface IProps {
	automationTemplate?: Api.IAutomationTemplate;
	className?: string;
	impersonationContext?: Api.IImpersonationContext;
	steps?: AutomationEditorStep[];
	styles?: StyleDeclarationValue[];
}

interface IState {
	trigger?: Api.IAutomationTrigger;
	advancedOptionsToggled: boolean;
	advancedCancelOptions: boolean;
	cancelCriteria: Api.IContactFilterCriteria[];
	viewCriteriaFlyout: boolean;
}
const AutomationTriggerCard = (props: IProps) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { className, styles, automationTemplate, impersonationContext } = props;
	const { logApiError } = useEventLogging('AutomationTriggerCard');

	const sendToHouseholdMutation = useAutomationTemplateSendToHouseholdMutation({
		onError: error => {
			logApiError('AutomationTriggerCard', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Send to household setting updated successfully');
		},
	});

	const allowUnsubscribedMutation = useAutomationTemplateAllowUnsubscribedMutation({
		onError: error => {
			logApiError('AutomationTriggerCard', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Allow unsubscribed setting updated successfully');
		},
	});

	const updateCriteria = useCancelCriteriaContactFilterMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Automation cancel criteria updated successfully');
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logApiError('UpdateCancelCriteriaContactFilter-Error', error);
		},
	});

	const addTriggerMutation = useAutomationTemplateAddTriggerMutation({
		onError: error => {
			logApiError('AddTrigger-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success('Automation trigger set successfully');
		},
	});

	const [state, setState] = React.useState<IState>({
		trigger: undefined,
		advancedOptionsToggled: false,
		advancedCancelOptions: false,
		cancelCriteria: [],
		viewCriteriaFlyout: false,
	});

	const trigger = React.useMemo(() => {
		return automationTemplate?.draft?.trigger || automationTemplate?.published?.trigger;
	}, [automationTemplate?.draft?.trigger, automationTemplate?.published?.trigger]);

	const onViewCriteria = React.useCallback((val: boolean) => {
		updateState({
			viewCriteriaFlyout: val,
		});
	}, []);

	React.useEffect(() => {
		if (automationTemplate?.contactFilter?.criteria?.length) {
			updateState({
				advancedOptionsToggled: true,
				advancedCancelOptions: true,
				cancelCriteria: automationTemplate.contactFilter.criteria,
			});
		}
	}, [automationTemplate?.contactFilter?.criteria]);

	React.useEffect(() => {
		updateState({
			trigger,
		});
	}, [trigger]);

	const setTrigger = async (_trigger: Api.IAutomationTrigger, fallbackTrigger?: Api.IAutomationTrigger) => {
		if (!Api.isValidAutomationTrigger(_trigger)) {
			updateState({
				trigger: _trigger,
			});
			return;
		}

		if (!automationTemplate?.id) {
			return;
		}

		try {
			await addTriggerMutation.mutateAsync({
				id: automationTemplate.id,
				trigger: _trigger,
				impersonationContext,
			});
			updateState({
				trigger: _trigger,
			});
		} catch {
			if (fallbackTrigger) {
				updateState({
					trigger: fallbackTrigger,
				});
			}
		}
	};

	const handleSaveCriteria = (criteria: Api.IContactFilterCriteria[]) => {
		if (!automationTemplate?.id) {
			return;
		}
		const newState: Partial<IState> = {
			viewCriteriaFlyout: false,
		};
		if (!criteria.length) {
			newState.advancedCancelOptions = false;
			return;
		}

		newState.cancelCriteria = criteria;
		updateCriteria.mutate({ id: automationTemplate.id, cancelCriteria: criteria });
		newState.advancedCancelOptions = true;
		updateState(newState);
	};

	const updateState = (newState: Partial<IState>) => {
		setState(prevState => ({
			...prevState,
			...newState,
		}));
	};

	const onDismissFlyout = () => {
		const nextState: Partial<IState> = {
			viewCriteriaFlyout: false,
		};
		if (!state.cancelCriteria?.length) {
			nextState.advancedCancelOptions = false;
		}
		updateState(nextState);
	};

	const handleAdvancedCancelOptionsChange = async () => {
		if (!automationTemplate?.id) {
			return;
		}
		const nextState: Partial<IState> = {
			advancedCancelOptions: !state.advancedCancelOptions,
		};
		if (!state.advancedCancelOptions && !state.cancelCriteria?.length) {
			nextState.viewCriteriaFlyout = true;
		}
		if (!nextState.advancedCancelOptions && state.cancelCriteria?.length) {
			updateCriteria.mutate({ id: automationTemplate.id, cancelCriteria: [] });

			nextState.cancelCriteria = [];
		}
		updateState(nextState);
	};

	const handleSendToHouseholdCheckChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		if (!automationTemplate?.id) {
			return;
		}
		sendToHouseholdMutation.mutate({
			id: automationTemplate.id,
			sendToHousehold: ev.target.checked,
			impersonationContext,
		});
	};

	const handleAllowUnsubscribedCheckChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		if (!automationTemplate?.id) {
			return;
		}
		allowUnsubscribedMutation.mutate({
			id: automationTemplate.id,
			allowUnsubscribed: ev.target.checked,
			impersonationContext,
		});
	};

	const isLoaded = !!automationTemplate?.id;
	const householdsEnabled = impersonationContext
		? impersonationContext.account?.features?.households?.enabled
		: userSession.account.features.households?.enabled;

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} automation-trigger-card ${className || ''}`}>
			<article className={css(styleSheet.cardHeader)}>
				<h4 className={css(styleSheet.cardHeaderText)}>Automation Type:</h4>
				{isLoaded ? (
					<div className={css(baseStyleSheet.horizontalStack)}>
						<AutomationTriggerIcon trigger={state.trigger} />
						<div>
							{Api.VmUtils.Automations.triggers.getAutomationTriggerTitle(
								state.trigger,
								impersonationContext || userSession
							)}
						</div>
					</div>
				) : (
					<LoadingSpinner type='small' />
				)}
			</article>
			<div className={css(styleSheet.cardBody)}>
				{isLoaded ? (
					<>
						{!!Api.VmUtils.Automations.triggers.triggerHasOptions(state.trigger) && (
							<AutomationTriggerSettings
								automationTemplate={automationTemplate}
								impersonationContext={impersonationContext}
								onRequestSetTrigger={setTrigger}
								trigger={state.trigger}
							/>
						)}
						<AutomationCancelOnReplyCheckbox
							automationTemplate={automationTemplate}
							impersonationContext={impersonationContext}
						/>
						{householdsEnabled ? (
							<Checkbox
								checked={automationTemplate?.sendToHousehold === true}
								className={css(styleSheet.sendToHouseholdsCheckbox)}
								disabled={sendToHouseholdMutation.isPending}
								id='automation-send-to-household-checkbox'
								onChange={handleSendToHouseholdCheckChange}
								type='large'
							>
								<div>
									<div className={css(styleSheet.checkboxText)}>Send to Households for this automation.</div>
								</div>
							</Checkbox>
						) : null}
					</>
				) : null}
				<div>
					<AdvancedOptionsButton
						isCollapsed={!state.advancedOptionsToggled}
						onClick={() =>
							updateState({
								advancedOptionsToggled: !state.advancedOptionsToggled,
							})
						}
					/>
					<Collapsible isCollapsed={!state.advancedOptionsToggled}>
						<>
							<Checkbox
								id='advancedCancelOptions'
								checked={state.advancedCancelOptions}
								type='large'
								onChange={handleAdvancedCancelOptionsChange}
							>
								<div>
									<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
										Cancel if contact no longer meets criteria
									</p>
								</div>
							</Checkbox>
							{state.cancelCriteria?.length ? (
								<button className={css(styleSheet.ctaButton)} onClick={() => onViewCriteria(true)}>
									View Criteria
								</button>
							) : null}
							{/* Show allowUnsubscribed option only for manual automations.
								Manual automations have no trigger (_type is undefined), while other 
								automation types have specific triggers like NewClient, Tag, etc. */}
							{!state.trigger?._type ? (
								<>
									<Checkbox
										checked={!!automationTemplate?.allowUnsubscribed}
										className={css(styleSheet.allowUnsubscribedCheckbox)}
										disabled={allowUnsubscribedMutation.isPending}
										id='automation-allow-unsubscribed-checkbox'
										onChange={handleAllowUnsubscribedCheckChange}
										type='large'
										aria-describedby='automation-allow-unsubscribed-helper'
									>
										<div>
											<div className={css(styleSheet.checkboxText)}>Allow sending to unsubscribed contacts</div>
										</div>
									</Checkbox>
									<div id='automation-allow-unsubscribed-helper' className={css(styleSheet.checkboxHelperText)}>
										Enable ONLY for critical transactional communications to stay in compliance with CAN-SPAM.
									</div>
								</>
							) : null}
						</>
					</Collapsible>
				</div>
			</div>
			<FlyoutModal useDefaultHeader={false} onRequestClose={onDismissFlyout} isOpen={state.viewCriteriaFlyout}>
				<AdvancedFiltersContextProvider
					initialFilterCriteria={state.cancelCriteria}
					refresh={state.viewCriteriaFlyout}
					onSave={handleSaveCriteria}
				>
					<AdvancedFiltersLegacyUI impersonationContext={impersonationContext} onDismissFlyout={onDismissFlyout} />
				</AdvancedFiltersContextProvider>
			</FlyoutModal>
		</div>
	);
};

export { AutomationTriggerCard };
